import * as React from "react";
import { Segment, Step, Dimmer, Loader, Container, StepGroup, StepContent, StepTitle } from "@/components/bp/bp-ui";
import { Information } from "./Information";
import { Review } from "./Review";
import { Parameters } from "./Parameters";
import { CaseRunClient, CaseRunCreateModelBase, QueriesClient } from "@app/api2/api";
import { HandlerRegistry } from "./RunTypes/HandlerRegistry";
import { PageHeader } from "@common/PageHeader";
import { insertComponentsIntoText } from "@common/Utils";
import BPLoading from "@common/BPLoading";
import { TranslationContext } from "../Contexts/TranslationContext";
import { IRouter, RouteContent } from "@common/RouteContent";
import { Navigate, Route, Routes } from "react-router";
import { BPContainer } from "@/components/bp/bpcontainer";

export interface ICreateRenderer<TModel extends CaseRunCreateModelBase, TFormValues> {
    canHandle(runType: string): boolean;
    createBlankModel(props: { labId: number, caseId: number, runType: string, evidenceId?: number, ednaTestId?: number }): Promise<TModel>;
    renderInformation(model: TModel, onComplete: (values: any) => void, router: any, rangeOfContributors: boolean);
    renderReview(model: TModel, onComplete: (values: any) => void, router: any);
    save(props: { labId: number, caseId: number, model: TModel });
    applyInformationValuesToModel: (model: TModel, values: TFormValues) => TModel;
    renderResults?: (props: { labId: number, caseId: number, caseRunId: number }) => React.ReactElement;
    renderRunList?: (props: { canCreateEvidence?: boolean, labId: number, caseId: number }) => React.ReactElement;
    hasOwnRunList?: () => boolean;
    cloneCaseRun?: (props: { labId: number, caseId: number, copyFrom: number }) => Promise<TModel>;
    propsComplete: (model: TModel | any) => void;
}

interface ICaseRunCreateProps {
    runType?: string;
    copyFrom?: number;
    labId: number;
    caseId: number;
    // baseUrl: string;
    aic: boolean;
    evidenceId?: number;
    ednaTestId?: number;
}

class CaseRunCreateState {
    model: CaseRunCreateModelBase;
    step: string = "information";
    loading: boolean = true;
    showServerErrors: boolean = false;
    serverErrors: string[] = [];
    disableStutter: boolean = false;
    secondaryParametersCollapsed: boolean = false;
}

export class CaseRunCreate extends React.Component<ICaseRunCreateProps, CaseRunCreateState> {
    caseRunClient = new CaseRunClient();
    queriesClient = new QueriesClient();

    constructor(props, context) {
        super(props, context);
        this.state = new CaseRunCreateState();
    }

    async componentDidMount() {
        let settings = await this.queriesClient.getSettings();

        if (this.props.copyFrom) {

            if (this.props.aic) {
                this.caseRunClient.cloneAicData(this.props.labId, this.props.caseId, this.props.copyFrom)
                    .then((r) => {
                        this.setState({ model: r, loading: false, secondaryParametersCollapsed: settings.secondaryParametersCollapsed });
                    });
            }
            else {
                let handler = this.getRunTypeHandler(this.props.runType);
                if (handler && 'cloneCaseRun' in handler) {
                    handler.cloneCaseRun({ labId: this.props.labId, caseId: this.props.caseId, copyFrom: this.props.copyFrom })
                        .then((r) => {
                            this.setState({ model: r, loading: false, secondaryParametersCollapsed: settings.secondaryParametersCollapsed });
                        });
                }
                else {
                    this.caseRunClient.cloneData(this.props.labId, this.props.caseId, this.props.copyFrom)
                        // CaseRunApi.getClone(this.props.labId, this.props.caseId, this.props.copyFrom)
                        .then((r) => {
                            this.setState({ model: r, loading: false, secondaryParametersCollapsed: settings.secondaryParametersCollapsed });
                        });
                }
            }
        } else {
            let handler = this.getRunTypeHandler(this.props.runType);
            if (handler)
                handler.createBlankModel({ labId: this.props.labId, caseId: this.props.caseId, runType: this.props.runType, evidenceId: this.props.evidenceId, ednaTestId: this.props.ednaTestId })
                    .then((r) => {
                        this.setState({ model: r, loading: false, secondaryParametersCollapsed: settings.secondaryParametersCollapsed });
                    });
        }
    }

    informationComplete = (values: any, routerProps: IRouter) => {
        let model = {
            ...this.state.model,
        } as CaseRunCreateModelBase;

        let handler = this.getRunTypeHandler(this.state.model.runType);
        if (handler) {
            let model2 = handler.applyInformationValuesToModel(model as any, values);

            // console.log(model2)
            this.setState({
                model: model2,
                disableStutter: false, //(values.hpUnknowns >= 4) || (values.hdUnknowns >= 4),
            });
        }

        // routerProps.history.push(`${this.props.runType}/parameters`);

        routerProps.navigate('../parameters');
    }

    propsComplete = (values: any, routerProps: IRouter) => {
        // console.log(values)
        let model = {
            ...this.state.model,
            parameters: this.state.model.parameters.map(p => { return { ...p, value: values[p.parameter] } })
        };

        let handler = this.getRunTypeHandler(model.runType);
        if (handler)
            handler.propsComplete(model);

        this.setState({
            model: model,
        });

        // routerProps.history.push(`${this.props.runType}/review`);
        routerProps.navigate('../review');


    }

    reviewComplete = () => {
        // console.log(this.state.model)
        this.setState({ loading: true });
        let handler = this.getRunTypeHandler(this.state.model.runType);
        if (handler)

            handler.save({ labId: this.props.labId, caseId: this.props.caseId, model: this.state.model as any })
                .then((r) => {
                    if (r.success) {
                        window.location.assign(r.url);
                    } else {
                        // console.log(r);
                        // if ((r as BPBadRequestResult).modelErrors)
                        //     (this.formik as RefObject<any>).current.setErrors((error as BPBadRequestResult).modelErrors)
                        this.setState(
                            {
                                ...this.state,
                                loading: false,
                                showServerErrors: true,
                                serverErrors: r.errors
                            });
                    }
                }).catch(error => {
                    // console.log(error);
                    this.setState(
                        {
                            ...this.state,
                            loading: false,
                            showServerErrors: true,
                            serverErrors: error.errors
                        });

                });


        // new CaseRunClient().create(this.props.labId, this.props.caseId, this.state.model)
        //     .then((r) => {
        //         if (r.success) {
        //             window.location.assign(r.url);
        //         } else {
        //             this.setState(
        //                 {
        //                     ...this.state,
        //                     loading: false,
        //                     showServerErrors: true,
        //                     serverErrors: r.errors
        //                 });
        //         }
        //     });
    }

    renderHeader(props) {
        return (<TranslationContext.Consumer>{t =>
            <StepGroup attached="top" widths={3}>
                <Step active={props.location.pathname.endsWith("/information")} /*onClick={() => { props.history.push("/information") }}*/>
                    <StepContent>
                        <StepTitle>{t.caseRun.runInformation}</StepTitle>
                    </StepContent>
                </Step>
                <Step active={props.location.pathname.endsWith("/parameters")} /*onClick={() => { props.history.push("/parameters") }}*/>
                    <StepContent>
                        <StepTitle>{t.common.parameters}</StepTitle>
                    </StepContent>
                </Step>
                <Step active={props.location.pathname.endsWith("/review")} /*onClick={() => { props.history.push("/review") }}*/>
                    <StepContent>
                        <StepTitle>{t.caseRun.review}</StepTitle>
                    </StepContent>
                </Step>
            </StepGroup>}</TranslationContext.Consumer>);
    }

    parameterRenderer = () => {
        return this.getRunTypeHandler(this.state.model.runType);
    }

    getRunTypeHandler(runType: string) {
        return HandlerRegistry.getRunTypeHandler(runType);
    }

    render() {
        if (this.state.model == null)
            return <BPLoading />
        else
            return (<TranslationContext.Consumer>{t =>
                // <BrowserRouter basename={this.props.baseUrl}>
                <BPContainer>
                    <Dimmer inverted active={this.state.loading}>
                        <Loader inverted>{t.caseRun.loadingData}</Loader>
                    </Dimmer>
                    {!this.props.copyFrom &&
                        <PageHeader
                            header={insertComponentsIntoText(t.caseRunView.newRun, { '{caseName}': this.state.model.caseName })}
                        />
                    }
                    {this.props.copyFrom &&
                        <PageHeader
                            header={insertComponentsIntoText(t.caseRunView.copyRun, { '{caseName}': this.state.model.caseName, '{oldRunId}': this.props.copyFrom })}
                        />
                    }
                    {/* {this.props.aic &&
                            <PageHeader
                                header={insertComponentsIntoText(t.caseRunView.newAicRun, { '{caseName}': this.state.model.caseName, '{oldRunId}': this.props. })}
                            />
                        } */}
                    <RouteContent render={(props) => this.renderHeader(props)} />
                    <Segment attached>
                        {!this.state.loading && <div>
                            <Routes>
                                <Route path="" element={<Navigate to={`information`} replace />} />
                                <Route path={`information`} element={<RouteContent render={(router) => <Information rangeOfContributors={this.state.model.runType == 'maxlr_aic' || this.state.model.runType == 'deconv_main'} model={this.state.model} onComplete={(values) => this.informationComplete(values, router)} router={router} />} />} />
                                <Route path={`parameters`} element={<RouteContent render={(router) => <Parameters secondaryParametersCollapsed={this.state.secondaryParametersCollapsed} parameterRenderer={this.parameterRenderer()} disableStutter={this.state.disableStutter} model={this.state.model} onComplete={(value) => this.propsComplete(value, router)} aic={false} />} />} />
                                <Route path={`review`} element={<RouteContent render={(router) => <Review model={this.state.model} onComplete={this.reviewComplete} router={router} />} />} />
                            </Routes>
                        </div>}
                    </Segment>
                </BPContainer>
                // </BrowserRouter>
            }</TranslationContext.Consumer>);
    }
}

